import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface Etudiant {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  nom_complet?: string;
  sexe?: 'M' | 'F';
  date_naissance?: string;
  lieu_naissance?: string;
  niveau: 'L1' | 'L2' | 'L3' | 'M1' | 'M2';
  parcours?: string;
  option?: string;
  pole?: string;
  classe?: string;
  annee_niveau?: number;
  telephone?: string;
  email?: string;
  photo_path?: string;
  photo_url?: string;
  type_formation?: string;
  type_formation_display?: string;
  classe_originale?: string;
  actif?: boolean;
  date_import?: string;
}

export interface ExcelPreviewResponse {
  headers: string[];
  preview_rows: any[][];
  total_rows: number;
  mapping_suggere: Record<number, string>;
  champs_obligatoires: string[];
  champs_optionnels: string[];
}

export interface ImportExcelRequest {
  file: File;
  mapping: Record<string, number>;
  annee_academique: string;
}

export interface ImportResult {
  success: boolean;
  historique_id: number;
  rapport: {
    nb_total_lignes: number;
    nb_etudiants_crees: number;
    nb_etudiants_maj: number;
    nb_erreurs: number;
    erreurs: any[];
    warnings: any[];
    mapping_utilise: Record<string, number>;
    date_import: string;
  };
}

export interface HistoriqueImport {
  id: number;
  annee_academique: string;
  fichier_nom: string;
  fichier_taille: number;
  nb_total_lignes: number;
  nb_etudiants_crees: number;
  nb_etudiants_maj: number;
  nb_erreurs: number;
  rapport_json: any;
  importe_par: any;
  date_import: string;
}

export interface ChoixFormulaire {
  niveaux: Array<{ code: string; label: string; count: number }>;
  options: Array<{ code: string; label: string; count: number }>;
  poles: Array<{ code: string; label: string; count: number }>;
  classes: Array<{ code: string; label: string; count: number }>;
  parcours: Array<{ code: string; label: string; count: number }>;
  niveaux_choix: Array<{ code: string; label: string }>;
  options_choix: Array<{ code: string; label: string }>;
  poles_choix: Array<{ code: string; label: string }>;
  poles_niveaux: Array<{ pole: string; label: string; niveaux: Record<string, number> }>;
  cs_dd_par_niveau?: Record<string, { CS: number; DD: number }>;
  cs_dd_par_pole?: Record<string, { CS: number; DD: number; label: string }>;
  cs_dd_par_pole_niveau?: Record<string, {
    label: string;
    CS: number;
    DD: number;
    niveaux: Record<string, { CS: number; DD: number }>;
  }>;
  cs_total?: number;
  dd_total?: number;
}

export interface SupprimerMultipleResult {
  success: boolean;
  nb_supprimes: number;
  message: string;
}

export interface ViderBaseResult {
  success: boolean;
  nb_supprimes: number;
  message: string;
}

export interface DoublonEtudiant {
  id: number;
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  niveau: string;
  date_naissance: string | null;
  score: number;
  score_nom: number;
  raisons: string[];
}

export interface DoublonGroupe {
  principal: {
    id: number;
    matricule: string;
    nom: string;
    prenoms: string;
    classe: string;
    niveau: string;
    date_naissance: string | null;
  };
  doublons: DoublonEtudiant[];
  score_max: number;
}

export interface DoublonsResponse {
  groupes: DoublonGroupe[];
  total_doublons: number;
  total_groupes: number;
  seuil_utilise: number;
  total_etudiants_analyses: number;
}

export interface FusionResult {
  success: boolean;
  message: string;
  transferts: {
    notes_devoirs: number;
    notes_examens: number;
  };
  doublon_desactive: string;
}

export interface PhotoImportResult {
  total: number;
  associees: number;
  non_associees: number;
  details: Array<{
    fichier: string;
    status: string;
    matricule?: string;
    nom?: string;
    message?: string;
  }>;
}

// ─── Types Mise à jour ──────────────────────────────────────────────────────

export interface MajAlerte {
  type: 'success' | 'warning' | 'danger' | 'info';
  message: string;
}

export interface ChangementClasse {
  matricule: string;
  nom: string;
  prenoms: string;
  ancienne_classe: string;
  nouvelle_classe: string;
  ancien_pole: string;
  nouveau_pole: string;
  ancienne_option: string;
  nouvelle_option: string;
  ancien_niveau: string;
  nouveau_niveau: string;
  ancien_parcours: string;
  nouveau_parcours: string;
  ancien_type: string;
  nouveau_type: string;
  etudiant_id: number;
}

export interface NouvelEtudiant {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  pole: string;
  option: string;
  parcours: string;
  niveau: string;
  type_formation: string;
  sexe: string;
  date_naissance: string | null;
  lieu_naissance: string;
}

export interface ClasseInconnue {
  classe: string;
  count: number;
  exemples: { matricule: string; nom: string; prenoms: string }[];
}

export interface AnalyseMajResponse {
  alertes: MajAlerte[];
  stats: {
    total_lignes: number;
    inchanges: number;
    nouveaux: number;
    changements_classe: number;
    classes_inconnues: number;
    erreurs: number;
  };
  changements_classe: ChangementClasse[];
  nouveaux: NouvelEtudiant[];
  classes_inconnues: ClasseInconnue[];
  erreurs: { ligne: number; erreur: string }[];
  colonnes_detectees: Record<string, number>;
}

export interface AppliquerMajResponse {
  success: boolean;
  nb_mis_a_jour: number;
  nb_crees: number;
  nb_erreurs: number;
  details: { type: string; matricule?: string; nom?: string; message?: string; ancien?: string; nouveau?: string; classe?: string }[];
  message: string;
}

export const etudiantsApi = createApi({
  reducerPath: 'etudiantsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Etudiants'],
  endpoints: (builder) => ({
    getEtudiants: builder.query<Etudiant[], Record<string, any> | void>({
      query: (params) => ({
        url: '/etudiants/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        // Gérer la réponse paginée ou directe
        if (response && response.results) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Etudiants'],
    }),

    getEtudiantById: builder.query<Etudiant, number>({
      query: (id) => `/etudiants/${id}/`,
      providesTags: ['Etudiants'],
    }),

    getEtudiantParMatricule: builder.query<Etudiant, string>({
      query: (matricule) => `/etudiants/par-matricule/${matricule}/`,
      providesTags: (_r, _e, matricule) => [{ type: 'Etudiants' as const, id: matricule }],
    }),

    // Import Excel endpoints
    previewExcel: builder.mutation<ExcelPreviewResponse, FormData>({
      query: (formData) => ({
        url: '/etudiants/preview_excel/',
        method: 'POST',
        body: formData,
      }),
    }),

    importExcel: builder.mutation<ImportResult, { file: File; mapping: Record<string, number>; annee_academique: string }>({
      query: ({ file, mapping, annee_academique }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mapping', JSON.stringify(mapping));
        formData.append('annee_academique', annee_academique);

        return {
          url: '/etudiants/import_excel/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Etudiants'],
    }),

    getHistoriqueImports: builder.query<HistoriqueImport[], { annee_academique?: string; limit?: number }>({
      query: (params) => ({
        url: '/etudiants/historique_imports/',
        params,
      }),
    }),

    getChoixFormulaire: builder.query<ChoixFormulaire, { type_formation?: string; exclure_type_formation?: string } | void>({
      query: (params) => ({
        url: '/etudiants/choix_formulaire/',
        params: params ?? undefined,
      }),
    }),

    // Suppression multiple
    supprimerMultiple: builder.mutation<SupprimerMultipleResult, number[]>({
      query: (ids) => ({
        url: '/etudiants/supprimer_multiple/',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['Etudiants'],
    }),

    // Vider la base
    viderBase: builder.mutation<ViderBaseResult, void>({
      query: () => ({
        url: '/etudiants/vider_base/',
        method: 'POST',
        body: { confirmer: true },
      }),
      invalidatesTags: ['Etudiants'],
    }),

    // Bascule spécialisation L3DI&GL — listing
    listerL3DIGLABasculer: builder.query<{
      results: Array<{
        id: number; matricule: string; nom: string; prenoms: string;
        classe: string; option: string; photo_url: string | null;
      }>;
      total: number;
      etiquette_origine: string;
      option_origine: string;
    }, void>({
      query: () => '/etudiants/l3-digl-a-basculer/',
      providesTags: ['Etudiants'],
    }),

    // Bascule spécialisation L3DI&GL — exécution
    basculerSpecialisation: builder.mutation<{
      success: boolean;
      etudiant: { id: number; matricule: string; classe: string; option: string };
      migration: {
        specialisation: string;
        ues_cible_disponibles: number;
        matieres_cible_disponibles: number;
        notes_devoirs_reattachees: number;
        notes_examens_reattachees: number;
        resultats_ecue_reattaches: number;
        resultats_ue_reattaches: number;
      };
    }, { id: number; specialisation: 'DI' | 'GL' }>({
      query: ({ id, specialisation }) => ({
        url: `/etudiants/${id}/basculer-specialisation/`,
        method: 'POST',
        body: { specialisation },
      }),
      invalidatesTags: ['Etudiants'],
    }),

    // Liste inscrits (sans pagination, pour impression)
    getListeInscrits: builder.query<Etudiant[], {
      niveau?: string;
      classe?: string;
      pole?: string;
      type_formation?: string;
      parcours?: string;
      option?: string;
      ordering?: string;
    } | void>({
      query: (params) => ({
        url: '/etudiants/liste_inscrits/',
        params: params ?? undefined,
      }),
      providesTags: ['Etudiants'],
    }),

    // Import photos en masse
    importerPhotos: builder.mutation<PhotoImportResult, { photos: File[]; annee_academique: string }>({
      query: ({ photos, annee_academique }) => {
        const formData = new FormData();
        photos.forEach((photo) => {
          formData.append('photos', photo);
        });
        formData.append('annee_academique', annee_academique);
        return {
          url: '/etudiants/importer_photos/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Etudiants'],
    }),

    // Détection de doublons
    checkDoublons: builder.query<DoublonsResponse, { seuil?: number; niveau?: string; classe?: string } | void>({
      query: (params) => ({
        url: '/etudiants/check_doublons/',
        params: params ?? undefined,
      }),
      providesTags: ['Etudiants'],
    }),

    // Fusion d'étudiants doublons
    fusionnerEtudiant: builder.mutation<FusionResult, { principalId: number; doublonId: number }>({
      query: ({ principalId, doublonId }) => ({
        url: `/etudiants/${principalId}/fusionner/`,
        method: 'POST',
        body: { doublon_id: doublonId },
      }),
      invalidatesTags: ['Etudiants'],
    }),

    // Mise à jour depuis Excel
    analyserMiseAJour: builder.mutation<AnalyseMajResponse, FormData>({
      query: (formData) => ({
        url: '/etudiants/analyser-mise-a-jour/',
        method: 'POST',
        body: formData,
      }),
    }),

    appliquerMiseAJour: builder.mutation<AppliquerMajResponse, {
      changements_confirmes: ChangementClasse[];
      nouveaux: NouvelEtudiant[];
      classes_manuelles?: Record<string, { pole: string; option: string; parcours: string; niveau: string; type_formation: string }>;
      annee_academique?: string;
    }>({
      query: (body) => ({
        url: '/etudiants/appliquer-mise-a-jour/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Etudiants'],
    }),
    detecterClassesDoublons: builder.query<{
      total_groupes: number;
      total_etudiants_a_fusionner: number;
      doublons: Array<{
        forme_normalisee: string;
        classe_principale: string;
        effectif_principal: number;
        variantes: Array<{ classe: string; effectif: number }>;
        total_a_fusionner: number;
      }>;
    }, void>({
      query: () => '/etudiants/detecter_classes_doublons/',
      providesTags: ['Etudiants'],
    }),

    fusionnerClasses: builder.mutation<{
      success: boolean;
      message: string;
      source: string;
      destination: string;
      nb_fusionnes: number;
    }, { source: string; destination: string }>({
      query: (data) => ({
        url: '/etudiants/fusionner_classes/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Etudiants'],
    }),
  }),
});

export const {
  useGetEtudiantsQuery,
  useGetEtudiantByIdQuery,
  useLazyGetEtudiantParMatriculeQuery,
  usePreviewExcelMutation,
  useImportExcelMutation,
  useGetHistoriqueImportsQuery,
  useGetChoixFormulaireQuery,
  useSupprimerMultipleMutation,
  useViderBaseMutation,
  useImporterPhotosMutation,
  useGetListeInscritsQuery,
  useCheckDoublonsQuery,
  useFusionnerEtudiantMutation,
  useAnalyserMiseAJourMutation,
  useAppliquerMiseAJourMutation,
  useDetecterClassesDoublonsQuery,
  useFusionnerClassesMutation,
  useListerL3DIGLABasculerQuery,
  useBasculerSpecialisationMutation,
} = etudiantsApi;
